// NodeView.tsx
import { FC, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	Link,
	Stack,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import { useTime } from "@/components/Application/TimeContext";
import { Translate } from "@/components/Application/Translate";
import DocumentSearchModal from "@/components/Procedures/EditNode/DocumentSearchModal";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import { AlertToast } from "@/helpers/alerts/AlertToast";

import { DocumentUpdateRequest } from "@/model/Procedure/DocumentUpdateRequest";
import { NodeEditSection } from "@/model/Procedure/EditNode";
import { ProcedureDetailNode } from "@/model/Procedure/ProcedureDetailNode";
import { DeleteProcedureNodeRequest } from "@/model/Procedure/ProcedureNodeDependency";
import {
	DocumentFromProcedure,
	DocumentSourceTypeAsNumber,
} from "@/model/Procedure/ProcedureUseDocument";

import {
	DeleteProcedureNode,
	UpdateProcedureNode,
	useMutateReimportDocument,
} from "@/service/ProcedureMutations";
import { useFetchDocumentForCurrentNode } from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

dayjs.extend(utc);

/* ───────────────── helpers ───────────────── */
function parseNodeEditSection(value: string | undefined): NodeEditSection {
	switch (value) {
		case "dependencies":
		case "document":
		case "document-upload":
		case "node":
			return value;
		default:
			return "";
	}
}

const NodeSchema = Yup.object({
	Name: Yup.string().trim().required("Required"),
	Tag: Yup.string().trim().nullable(),
});

export interface NodeViewProps {
	node: ProcedureDetailNode | undefined;
	edit: boolean;
	toggleEditNode: (editNode: boolean) => void;
}

/* ───────────────── component ───────────────── */
export const NodeView: FC<NodeViewProps> = (props) => {
	const { id, nodeEditSection } = useParams<RouteParams>();
	const procedureId = parseInt(id);
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const { timezone } = useTime();

	// (kept for parity – not used for conditional UI here, but safe to have)
	useState<NodeEditSection>(parseNodeEditSection(nodeEditSection));

	const [docSearchOpen, setDocSearchOpen] = useState(false);
	const [updateLoading, setUpdateLoading] = useState(false);
	const [documentUpdated, setDocumentUpdated] = useState(false);

	const deleteNode = DeleteProcedureNode();
	const updateNode = UpdateProcedureNode();
	const reimportDocument = useMutateReimportDocument();

	const {
		isLoading: docLoading,
		data: docData,
		refetch: refetchDocument,
	} = useFetchDocumentForCurrentNode({
		ProcedureId: procedureId,
		SchemeId: props.node?.SchemeId,
	});

	const [document, setDocument] = useState<
		DocumentFromProcedure | null | undefined
	>(undefined);

	useEffect(() => {
		refetchDocument().then((res) => setDocument(res.data?.Document));
	}, [props.node, refetchDocument]);

	useEffect(() => {
		if (docData?.Document && props.node) {
			const inDocument: DocumentFromProcedure = {
				FileId: props.node.FileId,
				FileName: props.node.FileName,
				Folder: props.node.FilePath,
				FileLinkId: 0,
				DuplicatedDocument: false,
				FileDate: props.node.FileUpdated,
				ExternalUniqueId: props.node.ExternalUniqueId,
				ProcedureList: docData.Document.ProcedureList,
				DocumentLocationType: docData.Document.DocumentLocationType,
				UrlSource: docData.Document.UrlSource,
				Source: docData.Document.Source,
			};
			setDocument(inDocument);
		}
	}, [docData?.Document, props.node]);

	const handleDelete = () => {
		const request: DeleteProcedureNodeRequest = {
			ProcedureId: procedureId,
			SchemeId: props.node?.SchemeId ?? 0,
			Checklists: props.node?.Children?.map((n) => n.CheckListId) ?? [],
		};

		try {
			// Show the confirmation dialog first
			AlertRequest.confirm(
				async (req) => {
					try {
						await deleteNode(req);
						props.toggleEditNode(false); // close after success
					} catch {
						AlertRequest.failed(); // handle delete failure
					}
				},
				request,
				props.node?.Name,
			);
		} catch {
			AlertRequest.failed(); // handle confirm dialog failure
		}
	};

	const initialValues = useMemo(
		() => ({
			ProcedureId: procedureId,
			Name: props.node?.Name ?? "",
			SchemeId: props.node?.SchemeId ?? props.node?.CheckListId,
			Tag: props.node?.NodeNumber ?? "",
		}),
		[procedureId, props.node],
	);

	const onSubmit = async (values: typeof initialValues) => {
		try {
			await updateNode(values);
			AlertToast.success();
			props.toggleEditNode(false);
		} catch {
			AlertToast.failed();
		}
	};

	const handleUpdateDocument = async () => {
		if (!document?.FileId && !props.node?.ExternalUniqueId) return;

		setUpdateLoading(true);
		try {
			await new Promise((r) => setTimeout(r, 400));
			const request: DocumentUpdateRequest = {
				FileId: document!.FileId,
				ExternalUniqueId: props.node!.ExternalUniqueId,
				ProcedureId: procedureId,
			};
			await reimportDocument(request);
			setDocumentUpdated(true);
			await refetchDocument();
		} catch (err) {
			console.error(err);
		} finally {
			setUpdateLoading(false);
			setTimeout(() => setDocumentUpdated(false), 1400);
		}
	};

	const prettyDocName = useMemo(() => {
		const full = document?.FileName ?? "";
		const idx = full.lastIndexOf("/") + 1;
		return full.substring(idx);
	}, [document?.FileName]);

	const nodeInfoFormId = "node-view-info-form";

	return (
		<Dialog
			open={props.edit}
			onClose={() => props.toggleEditNode(false)}
			fullWidth
			maxWidth="md"
			keepMounted={false}
			aria-labelledby="edit-node-dialog-title"
			scroll="paper"
			fullScreen={fullScreen}
			PaperProps={{
				sx: {
					borderRadius: { xs: 2, sm: 3 },
					boxShadow: 12,
				},
			}}
		>
			{/* Header */}
			<DialogTitle id="edit-node-dialog-title" sx={{
				pb: 0.5,
				pr: 6,
			}}>
				<IconButton
					aria-label="close"
					onClick={() => props.toggleEditNode(false)}
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
					}}
				>
					<CloseIcon />
				</IconButton>
				<Stack
					direction="row"
					alignItems="flex-start"
					justifyContent="space-between"
					gap={2}
				>
					<Box>
						<Typography variant="h6" fontWeight={700}>
							<Translate String="Procedure.Node.Header">
								Edit node
							</Translate>
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
							<Translate String="Procedure.Node.Subtitle">
								Update node metadata and attached document.
							</Translate>
						</Typography>
					</Box>

					{props.node?.NodeNumber ? (
						<Chip
							size="small"
							variant="outlined"
							label={
								<span>
									<Translate String="Procedure.Node.Information.NodeNumber">
										Node
									</Translate>
									: {props.node.NodeNumber}
								</span>
							}
						/>
					) : null}
				</Stack>
			</DialogTitle>

			<DialogContent dividers sx={{ pt: 2 }}>
				<Formik
					enableReinitialize
					initialValues={initialValues}
					validationSchema={NodeSchema}
					validateOnMount
					onSubmit={onSubmit}
				>
					{({
						values,
						errors,
						touched,
						handleChange,
						handleBlur,
						isValid,
						dirty,
						isSubmitting,
					}) => (
						<Form id={nodeInfoFormId}>
							<Grid container spacing={2.25}>
								{/* Name */}
								<Grid item xs={6}>
									<TextField
										id="NodeName"
										name="Name"
										size="small"
										required
										fullWidth
										label={
											<Translate String="Procedure.Node.Name">Name</Translate>
										}
										placeholder="e.g. Pump 14 inspection"
										value={values.Name}
										onChange={handleChange}
										onBlur={handleBlur}
										error={touched.Name && Boolean(errors.Name)}
										helperText={touched.Name && errors.Name ? errors.Name : " "}
									/>
								</Grid>

								{/* Two-column row */}
								<Grid item xs={6} sm={6}>
									<TextField
										id="NodeTagId"
										name="Tag"
										size="small"
										fullWidth
										label={
											<Translate String="Procedure.Node.Number">
												Node number
											</Translate>
										}
										placeholder="e.g. 2.1.3"
										value={values.Tag ?? ""}
										onChange={handleChange}
										onBlur={handleBlur}
									/>
								</Grid>

								<Grid item xs={12}>
									<Divider />
								</Grid>

								{/* Attached document panel */}
								<Grid item xs={12}>
									<Stack gap={1}>
										<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
											<Translate String="Procedure.Node.Edit.Document">
												Attached document
											</Translate>
										</Typography>

										<Box
											sx={{
												border: 1,
												borderColor: "divider",
												borderRadius: 2,
												p: 1.5,
												display: "flex",
												alignItems: "flex-start",
												justifyContent: "space-between",
												gap: 2,
												bgcolor: "background.paper",
											}}
										>
											<Box sx={{ minWidth: 0 }}>
												<Typography
													variant="body2"
													sx={{ fontWeight: 600 }}
													noWrap
												>
													{document
														? prettyDocName || "—"
														: docLoading
															? "Loading…"
															: "—"}
												</Typography>
												{document?.FileDate && (
													<Typography variant="caption" color="text.secondary">
														<Translate String="Procedure.Node.Document.Updated">
															Updated
														</Translate>
														:{" "}
														{dayjs
															.utc(document.FileDate)
															.tz(timezone)
															.format("DD/MM/YY HH:mm")}
													</Typography>
												)}
												{document?.UrlSource ? (

													<Box>
														<Link
															href={`/api/Documents/${document.FileId}/html`}
															target="_blank"
															rel="noopener noreferrer"
															underline="hover"
															variant="body2"
															sx={{
																display: "inline-flex",
																alignItems: "center",
																gap: 0.5,
																fontWeight: 500,
															}}
														>
															<Translate String="Procedures.UsedDocuments.Link">
																Open
															</Translate>
															<OpenInNewIcon sx={{ fontSize: 16 }} />
														</Link>
													</Box>

												) : null}
											</Box>

											<Stack direction="row" gap={1} flexShrink={0}>
												{document &&
													document.Source !== DocumentSourceTypeAsNumber.Local ? (
													<Tooltip
														title={
															<Translate String="Procedures.UsedDocuments.Update.Document">
																Update document
															</Translate>
														}
													>
														<span>
															<IconButton
																size="small"
																onClick={handleUpdateDocument}
																color={documentUpdated ? "success" : "default"}
																disabled={updateLoading}
																sx={{
																	border: 1,
																	borderColor: "divider",
																	borderRadius: 2,
																}}
															>
																{documentUpdated ? (
																	<CheckOutlinedIcon fontSize="small" />
																) : (
																	<CachedOutlinedIcon
																		fontSize="small"
																		sx={{
																			animation: updateLoading
																				? "spin 1s linear infinite"
																				: "none",
																			"@keyframes spin": {
																				"0%": { transform: "rotate(0deg)" },
																				"100%": { transform: "rotate(360deg)" },
																			},
																		}}
																	/>
																)}
															</IconButton>
														</span>
													</Tooltip>
												) : null}

												<Tooltip
													title={
														<Translate String="Procedures.UsedDocuments.Edit">
															Choose another
														</Translate>
													}
												>
													<IconButton
														size="small"
														onClick={() => setDocSearchOpen(true)}
														sx={{
															border: 1,
															borderColor: "divider",
															borderRadius: 2,
														}}
													>
														<EditOutlinedIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</Stack>
										</Box>
									</Stack>
								</Grid>

								{/* Compact meta row */}
								<Grid item xs={12}>
									<Stack direction="row" gap={1} flexWrap="wrap">
										{props.node?.Id != null && (
											<Badge
												label={<Translate String="Common.Id">ID</Translate>}
												value={String(props.node.Id)}
											/>
										)}
										{props.node?.GroupId != null && (
											<Badge
												label={
													<Translate String="Common.Group">Group</Translate>
												}
												value={String(props.node.GroupId)}
											/>
										)}
										{props.node?.ExternalUniqueId && (
											<Badge
												label="External ID"
												value={props.node.ExternalUniqueId}
											/>
										)}
									</Stack>
								</Grid>

								{/* hidden submit to allow Enter key */}
								<input type="submit" hidden />
							</Grid>

							{/* Footer inside Form so we can read state easily */}
							<DialogActions
								sx={{
									mt: 1,
									px: 0,
									pt: 2,
									justifyContent: "space-between",
								}}
							>
								{/* Left: delete */}
								<Button
									variant="outlined"
									color="error"
									onClick={handleDelete}
									startIcon={<DeleteOutlineIcon />}
									sx={{
										borderRadius: 3,
										px: 2,
										textTransform: "none",
										fontWeight: 700,
									}}
								>
									<Translate String="Node.Delete">Delete node</Translate>
								</Button>

								{/* Right: close + save */}
								<Stack direction="row" gap={1.25}>
									<Button
										variant="outlined"
										color="warning"
										onClick={() => props.toggleEditNode(false)}
										startIcon={<CloseOutlinedIcon />}
										sx={{
											borderRadius: 3,
											px: 2,
											textTransform: "none",
											fontWeight: 700,
										}}
									>
										<Translate String="Common.Close">Close</Translate>
									</Button>
									<Button
										type="submit"
										form={nodeInfoFormId}
										variant="contained"
										color="success"
										startIcon={<SaveOutlinedIcon />}
										disabled={!dirty || !isValid || isSubmitting}
										sx={{
											borderRadius: 3,
											px: 2.25,
											textTransform: "none",
											fontWeight: 700,
										}}
									>
										<Translate String="Common.Save">Save</Translate>
									</Button>
								</Stack>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</DialogContent>

			{/* Document picker */}
			<DocumentSearchModal
				open={docSearchOpen}
				onClose={() => setDocSearchOpen(false)}
				onComplete={() => props.toggleEditNode(false)}
				nodeId={props.node?.SchemeId ?? 0}
			/>
		</Dialog>
	);
};

/* ───────────────── small UI bits ───────────────── */
const Badge = ({ label, value }: { label: React.ReactNode; value: string }) => (
	<Stack
		direction="row"
		gap={0.75}
		sx={{
			px: 1.25,
			py: 0.5,
			border: 1,
			borderColor: "divider",
			borderRadius: 2,
			alignItems: "center",
			bgcolor: "background.paper",
		}}
	>
		<Typography variant="caption" color="text.secondary">
			{label}
		</Typography>
		<Typography variant="caption" sx={{ fontWeight: 700 }}>
			{value}
		</Typography>
	</Stack>
);
